import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import {
  deleteBodyOfWater,
  saveBodyOfWater,
  type BodyOfWater,
  type BodyOfWaterInput,
} from '@/features/service-locations/api/serviceLocations';

const empty: BodyOfWaterInput = {
  name: '',
  width: null,
  length: null,
  depth: null,
  volume: null,
  volumeUnit: 'gallons',
};
const display = (value: string | number | null | undefined) =>
  value == null ? '—' : String(Number(value) / 100);

export function PoolManager({
  clientId,
  locationId,
  pools,
  onChanged,
}: {
  clientId: number;
  locationId: number;
  pools: BodyOfWater[];
  onChanged: () => Promise<unknown>;
}) {
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState<BodyOfWater | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [form, setForm] = useState<BodyOfWaterInput>(empty);
  const save = useMutation({
    mutationFn: () => saveBodyOfWater(clientId, locationId, form, editing?.id),
    onSuccess: async () => {
      setDialog(false);
      setEditing(null);
      await onChanged();
    },
  });
  const remove = useMutation({
    mutationFn: (ids: number[]) =>
      Promise.all(ids.map((id) => deleteBodyOfWater(clientId, locationId, id))),
    onSuccess: async () => {
      setSelectedIds(new Set());
      await onChanged();
    },
  });
  const open = (pool?: BodyOfWater) => {
    setEditing(pool ?? null);
    setForm(
      pool
        ? {
            name: pool.name,
            width: pool.width == null ? null : Number(pool.width) / 100,
            length: pool.length == null ? null : Number(pool.length) / 100,
            depth: pool.depth == null ? null : Number(pool.depth) / 100,
            volume: pool.volume == null ? null : Number(pool.volume) / 100,
            volumeUnit: pool.volume_unit === 'litres' ? 'litres' : 'gallons',
          }
        : empty,
    );
    setDialog(true);
  };
  const allSelected = pools.length > 0 && pools.every((pool) => selectedIds.has(pool.id));
  const someSelected = pools.some((pool) => selectedIds.has(pool.id));
  const selectedPool =
    selectedIds.size === 1 ? (pools.find((pool) => selectedIds.has(pool.id)) ?? null) : null;
  const togglePool = (id: number) =>
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const numberField =
    (key: 'width' | 'length' | 'depth' | 'volume') =>
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setForm({ ...form, [key]: event.target.value === '' ? null : Number(event.target.value) });

  return (
    <>
      <Paper elevation={0} sx={{ ...paperSx, mt: 1.5, overflow: 'hidden' }}>
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          spacing={0.5}
          sx={{ minHeight: 50, px: 1.5 }}
        >
          <Button startIcon={<AddIcon />} onClick={() => open()} sx={commandButtonSx}>
            New Pool
          </Button>
          {selectedPool && (
            <Button
              startIcon={<EditOutlinedIcon />}
              onClick={() => open(selectedPool)}
              sx={commandButtonSx}
            >
              Edit
            </Button>
          )}
          {selectedIds.size > 0 && (
            <Button
              startIcon={<DeleteOutlineIcon />}
              disabled={remove.isPending}
              onClick={() => remove.mutate([...selectedIds])}
              sx={deleteCommandButtonSx}
            >
              Delete
            </Button>
          )}
        </Stack>
        <Divider />
        {remove.isError && (
          <Alert severity="error" sx={{ m: 2 }}>
            The selected {selectedIds.size === 1 ? 'pool' : 'pools'} could not be deleted.
          </Alert>
        )}
        <TableContainer>
          <Table
            size="small"
            aria-label="Pools"
            sx={{
              minWidth: 820,
              tableLayout: 'fixed',
              '& .MuiTableCell-root': {
                height: 48,
                py: 1.1,
                borderBottom: '1px solid #e1dfdd',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={!allSelected && someSelected}
                    onChange={() =>
                      setSelectedIds(
                        allSelected ? new Set() : new Set(pools.map((pool) => pool.id)),
                      )
                    }
                    inputProps={{ 'aria-label': 'Select all pools' }}
                  />
                </TableCell>
                <TableCell sx={{ width: '32%', fontWeight: 600 }}>Pool</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Width (m)</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Length (m)</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Depth (m)</TableCell>
                <TableCell sx={{ width: '18%', fontWeight: 600 }}>Volume</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!pools.length && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{ height: 150, color: 'text.secondary' }}
                  >
                    No pools have been configured.
                  </TableCell>
                </TableRow>
              )}
              {pools.map((pool) => (
                <TableRow
                  key={pool.id}
                  hover
                  selected={selectedIds.has(pool.id)}
                  role="link"
                  tabIndex={0}
                  aria-label={`Open ${pool.name}`}
                  onClick={() => open(pool)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      open(pool);
                    }
                  }}
                  sx={{
                    cursor: 'pointer',
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: -2,
                    },
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedIds.has(pool.id)}
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => togglePool(pool.id)}
                      inputProps={{ 'aria-label': `Select ${pool.name}` }}
                    />
                  </TableCell>
                  <TableCell>
                    <Link
                      component="button"
                      underline="none"
                      onClick={(event) => {
                        event.stopPropagation();
                        open(pool);
                      }}
                    >
                      {pool.name}
                    </Link>
                  </TableCell>
                  <TableCell>{display(pool.width)}</TableCell>
                  <TableCell>{display(pool.length)}</TableCell>
                  <TableCell>{display(pool.depth)}</TableCell>
                  <TableCell>
                    {pool.volume == null
                      ? '—'
                      : `${display(pool.volume)} ${pool.volume_unit ?? ''}`}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider />
        <Typography variant="body2" sx={{ px: 2, py: 1.25 }}>
          Rows: {pools.length}
        </Typography>
      </Paper>

      {dialog && (
        <Paper elevation={0} sx={{ ...paperSx, mt: 1.5, p: 2 }}>
          <Typography variant="h2" sx={{ mb: 1.5 }}>
            {editing ? 'Edit' : 'New'} pool
          </Typography>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Pool name"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              required
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                type="number"
                label="Width (m)"
                value={form.width ?? ''}
                onChange={numberField('width')}
              />
              <TextField
                type="number"
                label="Length (m)"
                value={form.length ?? ''}
                onChange={numberField('length')}
              />
              <TextField
                type="number"
                label="Depth (m)"
                value={form.depth ?? ''}
                onChange={numberField('depth')}
              />
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField
                type="number"
                label="Volume"
                value={form.volume ?? ''}
                onChange={numberField('volume')}
                fullWidth
              />
              <TextField
                select
                label="Unit"
                value={form.volumeUnit}
                onChange={(event) =>
                  setForm({ ...form, volumeUnit: event.target.value as 'gallons' | 'litres' })
                }
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="gallons">Gallons</MenuItem>
                <MenuItem value="litres">Litres</MenuItem>
              </TextField>
            </Stack>
            {save.isError && <Alert severity="error">The pool could not be saved.</Alert>}
          </Stack>
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              onClick={() => {
                setDialog(false);
                setEditing(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!form.name.trim() || save.isPending}
              onClick={() => save.mutate()}
            >
              Save
            </Button>
          </Stack>
        </Paper>
      )}
    </>
  );
}

const paperSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)',
};
const commandButtonSx = {
  color: 'text.primary',
  '& .MuiButton-startIcon': { color: 'primary.main' },
};
const deleteCommandButtonSx = {
  color: 'text.primary',
  '& .MuiButton-startIcon': { color: 'error.main' },
};
